import React from "react";
import { createRange, transpose } from "../../../utils/collectionUtils";
import {
  countDays,
  formatDate,
  getMonthName,
  getWeekNumbers,
} from "../../../utils/datesUtils";
import { DAYS_SHORTCUTS, getDayname } from "../../../utils/calculations";
import { useCalendarContext } from "./CalendarContext";

type DayShortcut = (typeof DAYS_SHORTCUTS)[number];

interface VlCalendarMonthProps {
  showWeeksNumber?: boolean;
  month: number;
  year: number;
  selectedDate?: { start: string | null; end: string | null };
  isSelected?: (date: string) => boolean;
  isDisabled?: (dateOrWeekday: string | number) => boolean;
  isOccupied?: (date: string) => boolean;
  isPartially?: (date: string) => boolean;
  customClasses?: Record<string, (date: string) => boolean>;
  firstDayOfWeek?: DayShortcut;
  isHovering?: boolean;
  maxDate?: string | null;
  lang?: string;
  onInput?: (date: string) => void;
  onHoveringUpdate?: (state: boolean, date: string) => void;
  className?: string;
  availableplan?: boolean;
  focusDay: Date;
}

export const VlCalendarMonth: React.FC<VlCalendarMonthProps> = ({
  showWeeksNumber = false,
  month,
  year,
  isSelected,
  isDisabled,
  isOccupied,
  isPartially,
  customClasses = {},
  firstDayOfWeek = "mo",
  isHovering = false,
  lang = "DE",
  onInput,
  onHoveringUpdate,
  className,
  availableplan = false,
  focusDay,
}) => {
  // Context (equivalent to Vue's inject)
  const { selectDate: dateType, disabledWeekdays } = useCalendarContext();

  // Computed values
  const monthName = getMonthName(month, lang);

  const days = createRange(1, countDays(month, year));

  const daysOffset = DAYS_SHORTCUTS.indexOf(firstDayOfWeek);
  const daysNames = transpose(getDayname(lang), daysOffset);

  const weekNumbers = getWeekNumbers(month, year);

  // Methods
  const getDate = (day: number): string => {
    return formatDate(day, month, year);
  };

  const getWeekDay = (date: string): number => {
    const dayNumber = new Date(date);
    return dayNumber.getDay();
  };
  const calculateIndex = (day: number): number => {
    if (availableplan) {
      return -1;
    }

    const datestring = getDate(day);
    const weekday = getWeekDay(datestring);

    if (
      (isDisabled && isDisabled(datestring)) ||
      (isDisabled && isDisabled(weekday))
    ) {
      return -1;
    }
    const date = new Date(year, month, day);

    if (focusDay.getTime() === date.getTime()) {
      return 0;
    } else {
      return -1;
    }
  };
  const calculateClasses = (day: number): string => {
    const classes: string[] = [];

    // Offset for first day
    if (day === 1) {
      const offset = (new Date(year, month, 1).getDay() + 7 - daysOffset) % 7;
      if (offset > 0) {
        classes.push(`orm-calendar-month__day--offset-${offset}`);
      }
    }
    const current = calculateIndex(day);

    if (current == 0) {
      classes.push("current");
    }
    const date = getDate(day);
    const weekday = getWeekDay(date);

    // Occupied
    if (isOccupied && isOccupied(date)) {
      classes.push("isOccupied");
    }

    // Partially
    if (isPartially && isPartially(date)) {
      classes.push("isPartially");
    }

    // Selected
    if (isSelected && isSelected(date)) {
      classes.push("selected");

      if (!isSelected(getDate(day - 1))) {
        classes.push("selected--first");
      }

      if (!isSelected(getDate(day + 1))) {
        classes.push("selected--last");
      }
    }

    // Disabled
    if (
      (isDisabled && isDisabled(date)) ||
      (isDisabled && isDisabled(weekday))
    ) {
      classes.push("disabled");

      if (!isDisabled(getDate(day - 1))) {
        classes.push("disabled--first");
      }

      if (!isDisabled(getDate(day + 1))) {
        classes.push("disabled--last");
      }
    }

    // Custom classes
    Object.keys(customClasses).forEach((cl) => {
      const fn = customClasses[cl];
      if (fn && fn(date)) {
        classes.push(cl);
      }
    });

    return classes.join(" ");
  };

  const handleDayClick = (day: number) => {
    if (onInput) {
      onInput(getDate(day));
    }
  };

  return (
    <div className={`orm-calendar-month ${className || ""}`}>
      <div className="orm-calendar-month__title">
        {monthName} {year}
      </div>

      <div className="orm-flex">
        {/* Week Numbers Column */}
        {showWeeksNumber && (
          <div className="orm-calendar-month__week-numbers-column">
            {weekNumbers.map((number) => (
              <div key={number} className="orm-calendar-month__week-number">
                {number}
              </div>
            ))}
          </div>
        )}

        <div>
          {/* Days Names Header */}
          <div className="orm-flex">
            {daysNames.map((name) => (
              <span key={name} className="orm-calendar-month__week-day">
                {name}
              </span>
            ))}
          </div>

          {/* Days Grid */}
          <div
            className={`orm-flex orm-flex-wrap ${isHovering ? "hovering" : ""}`}
          >
            {days.map((day) => (
              <span
                onMouseOver={() =>
                  onHoveringUpdate?.(true, `${day}-${month}-${year}`)
                }
                onMouseOut={() => onHoveringUpdate?.(false, "none")}
                key={day}
                onClick={() => handleDayClick(day)}
                className={`orm-calendar-month__day ${calculateClasses(day)}`}
                tabIndex={calculateIndex(day)}
                data-day={day}
                data-month={month}
                data-year={year}
              >
                {day}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VlCalendarMonth;
